"use client"

import { ref, computed } from "vue"
import api from '@/api'

const user = ref(null)
const isAuthenticated = ref(false)
const isLoading = ref(false)

// API base URL - adjust as needed
// const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

export function useAuth() {
  // API call helper
  // const apiCall = async (endpoint, options = {}) => {
  //   const defaultOptions = {
  //     credentials: 'include',  // Always include credentials
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //   }

  //   // Merge options, ensuring credentials and headers are properly combined
  //   const config = {
  //     ...defaultOptions,
  //     ...options,
  //     headers: {
  //       ...defaultOptions.headers,
  //       ...(options.headers || {})
  //     }
  //   }

  //   try {
  //       console.log(`${API_BASE_URL}${endpoint}`)
  //     const response = await fetch(`${API_BASE_URL}${endpoint}`, config)
  //     const data = await response.json()
      
  //     if (!response.ok) {
  //       // Handle authentication errors globally
  //       if (response.status === 401) {
  //         console.log('Authentication failed, logging out...')
  //         // Clear client-side auth state immediately
  //         user.value = null
  //         isAuthenticated.value = false
  //         localStorage.removeItem("authToken")
  //         localStorage.removeItem("userData")
          
  //         throw new Error('Session expired. Please log in again.')
  //       }
  //       throw new Error(data.message || 'API request failed')
  //     }
      
  //     return data
  //   } catch (error) {
  //     console.error('API Error:', error)
  //     throw error
  //   }
  // }

  const login = async (credentials) => {
    isLoading.value = true
    try {
      const response = await api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password
      })

      if (response.data.ok) {
        user.value = response.data.user_data
        isAuthenticated.value = true
        // Persist user session locally so refresh keeps user logged in
        localStorage.setItem("userData", JSON.stringify(response.data.user_data))
        return { 
          success: true, 
          user: response.data.user_data,
          message: response.data.message 
        }
      } else {
        // Ensure user is logged out on failed attempt
        logout()
        return { 
          success: false, 
          error: response.data.message || 'Login failed' 
        }
      }
    } catch (error) {
      logout()
      return { success: false, error: error.message }
    } finally {
      isLoading.value = false
    }
  }

  const signup = async (userData) => {
    isLoading.value = true
    try {
      const response = await api.post('/auth/signup', {
        email: userData.email,
        first_name: userData.firstName || userData.first_name,
        last_name: userData.lastName || userData.last_name,
        password: userData.password,
        role: userData.role || 'sales_rep'
      })

      return { success: true, user: response.data.user }
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    isLoading.value = true
    try {
      // Always inform backend to clear the session cookie
      try {
        await api.get('/logout')
      } catch (error) {
        console.warn('Logout API call failed:', error)
      }

      // Clear client-side auth state
      user.value = null
      isAuthenticated.value = false
      localStorage.removeItem("authToken")
      localStorage.removeItem("userData")

      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      isLoading.value = false
    }
  }

  const verifyToken = async () => {
    const token = localStorage.getItem("authToken")
    if (!token) return false

    try {
      const response = await api.get('/auth/verify')
      
      user.value = response.data.user
      isAuthenticated.value = true
      
      // Update stored user data
      localStorage.setItem("userData", JSON.stringify(response.data.user))
      
      return true
    } catch (error) {
      // Token is invalid, clear auth data
      logout()
      return false
    }
  }

  // Check if the user is still authenticated (runs on every refresh/route change)
  const checkAuth = async () => {
    isLoading.value = true
    const timestamp = new Date().toLocaleTimeString()
    
    try {
      console.log(`[${timestamp}] Checking authentication with backend...`)
      // Always verify with backend first to check if session is still valid
      const response = await api.get('/get_authenticated_user_data')
      
      if (!response.data.ok || !response.data.user_data) {
        console.warn(`[${timestamp}] Session invalid - no user data`)
        await logout()
        return false
      }

      // Validate user data
      const userData = response.data.user_data
      if (!userData.email || !userData.role) {
        console.warn(`[${timestamp}] Session invalid - incomplete user data`)
        await logout()
        return false
      }

      console.log(`[${timestamp}] Session valid, user authenticated:`, userData.email)
      user.value = userData
      isAuthenticated.value = true
      localStorage.setItem("userData", JSON.stringify(userData))
      return true
      
    } catch (error) {
      console.warn(`[${timestamp}] Session expired or invalid:`, error.message)
      // Clear all auth data on authentication failure
      await logout()
      return false
    } finally {
      isLoading.value = false
    }
  }

  const updateProfile = async (profileData) => {
    isLoading.value = true
    try {
      const response = await api.put('/auth/profile', profileData)

      if (response.data.ok && response.data.user) {
        // Update the local user state with the response data
        user.value = response.data.user
        isAuthenticated.value = true
        // Update the stored user data
        localStorage.setItem("userData", JSON.stringify(response.data.user))
        
        return { 
          success: true, 
          user: response.data.user,
          message: response.data.message 
        }
      } else {
        return { 
          success: false, 
          error: response.data.message || 'Failed to update profile' 
        }
      }
    } catch (error) {
      console.error('Profile update error:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update profile' 
      }
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    isLoading.value = true
    try {
      const response = await api.post('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      })

      if (response.data.ok) {
        return { 
          success: true, 
          message: response.data.message || 'Password updated successfully' 
        }
      } else {
        return { 
          success: false, 
          error: response.data.message || 'Failed to update password' 
        }
      }
    } catch (error) {
      console.error('Password change error:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update password' 
      }
    } finally {
      isLoading.value = false
    }
  }

  const getProfile = async () => {
    try {
      const response = await api.get('/auth/profile')
      return { success: true, user: response.data.user }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Admin functions
  const getUsers = async () => {
    try {
      const response = await api.get('/admin/users')
      return { success: true, users: response.data.users, countUsers : response.data.countUsers, pagination : response.data.pagination }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const createUser = async (userData) => {
    try {
      const response = await api.post('/admin/users', userData)
      return { success: true, user: response.data.user }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const updateUser = async (userId, userData) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData)
      return { success: true, user: response.data.user }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const resetUserPassword = async (userId, newPassword) => {
    try {
      const response = await api.post(`/admin/users/${userId}/reset-password`, { password: newPassword })
      return { success: true, message: response.data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getRoles = async () => {
    try {
      const response = await api.get('/admin/roles')
      return { success: true, roles: response.data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const createRole = async (roleData) => {
    try {
      const response = await api.post('/admin/roles', roleData)
      return { success: true, role: response.data.role, message: response.data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Role-based access control helpers
  const hasRole = (role) => {
    return user.value?.role === role
  }

  const hasAnyRole = (roles) => {
    return roles.includes(user.value?.role)
  }

  const isAdmin = computed(() => hasRole('admin'))
  const isManager = computed(() => hasRole('manager'))
  const isSalesRep = computed(() => hasRole('sales_rep'))
  const isViewer = computed(() => hasRole('viewer'))
  const isSuperAdmin = computed(() => hasRole('super_admin'))

  const canAccess = (requiredRoles) => {
    if (!isAuthenticated.value) return false
    if (typeof requiredRoles === 'string') {
      return hasRole(requiredRoles)
    }
    return hasAnyRole(requiredRoles)
  }

  const currentUser = computed(() => user.value)

  const forgotPassword = async (email) => {
  isLoading.value = true
  try {
    const response = await api.post('/auth/forgot-password', {
      email: email.trim().toLowerCase()
    })

    // Always return success for security (don't reveal if account exists)
    return { 
      success: true, 
      message: 'If an account exists with this email, reset instructions have been sent.' 
    }
  } catch (error) {
    return { 
      success: false, 
      error: 'Unable to process request. Please try again later.' 
    }
  } finally {
    isLoading.value = false
  }
}

const resetPassword = async (resetData) => {
  isLoading.value = true
  try {
    const response = await api.post('/auth/reset-password', {
      token: resetData.token,
      password: resetData.password
    })

    if (response.data.ok) {
      return { 
        success: true, 
        message: response.data.message || 'Password reset successfully' 
      }
    } else {
      return { 
        success: false, 
        error: response.data.message || 'Failed to reset password' 
      }
    }
  } catch (error) {
    return { 
      success: false, 
      error: error.response?.data?.message || 'Invalid or expired reset token' 
    }
  } finally {
    isLoading.value = false
  }
}


  return {
    // API helper
    // apiCall,

    // State
    user: currentUser,
    isAuthenticated: computed(() => isAuthenticated.value),
    isLoading: computed(() => isLoading.value),

    // Auth methods
    login,
    signup,
    logout,
    checkAuth,
    verifyToken,

    // Profile methods
    updateProfile,
    changePassword,
    getProfile,

    // Admin methods
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    getRoles,
    createRole,

    // Role-based access control
    hasRole,
    hasAnyRole,
    isAdmin,
    isManager,
    isSalesRep,
    isViewer,
    isSuperAdmin,
    canAccess,
    forgotPassword,
  resetPassword,

    // Role constants
    roles: {
      ADMIN: 'admin',
      MANAGER: 'manager',
      SALES_REP: 'sales_rep',
      VIEWER: 'viewer'
    }
  }
}
